import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { ChatMessage } from '../../contexts/ChatContext';
import FileUploadButton from './FileUploadButton';

interface ChatWidgetProps {
  errandId: string;
  errandTitle: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({
  errandId,
  errandTitle,
  isOpen = false,
  onToggle
}) => {
  const { messages, sendMessage, fetchMessages, markMessagesAsRead, loading, error } = useChat();
  const [newMessage, setNewMessage] = useState('');
  const [isMinimized, setIsMinimized] = useState(!isOpen);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch messages when widget opens
  useEffect(() => {
    if (!isMinimized) {
      fetchMessages(errandId);
      markMessagesAsRead(errandId);
    }
  }, [errandId, isMinimized, fetchMessages, markMessagesAsRead]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  // Handle sending message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || loading) return;

    const success = await sendMessage({
      errandId,
      message: newMessage.trim(),
      messageType: 'text'
    });

    if (success) {
      setNewMessage('');
      inputRef.current?.focus();
      // Show a subtle success indicator
      const button = inputRef.current?.nextElementSibling as HTMLButtonElement;
      if (button) {
        const originalText = button.innerHTML;
        button.innerHTML = '✓';
        button.className = 'px-4 py-2 bg-green-600 text-white rounded-lg transition-colors text-sm';
        setTimeout(() => {
          button.innerHTML = originalText;
          button.className = 'px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm';
        }, 1000);
      }
    }
  };

  // Toggle widget state (minimize/expand)
  const toggleWidget = () => {
    const newState = !isMinimized;
    setIsMinimized(newState);
    // Don't call onToggle for minimize - only for complete close
  };

  // Close widget completely
  const closeWidget = () => {
    setIsMinimized(true);
    onToggle?.(false); // This will trigger the parent to remove the widget
  };

  // Format message time
  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get message bubble styling
  const getMessageStyle = (message: ChatMessage) => {
    // Get user ID from JWT token to determine if it's own message
    const token = localStorage.getItem('token');
    let isOwnMessage = false;

    if (token) {
      try {
        // Decode JWT token to get user ID
        const payload = JSON.parse(atob(token.split('.')[1]));
        isOwnMessage = payload.id === message.sender_id;
      } catch (error) {
        console.error('Error decoding JWT token:', error);
      }
    }

    return {
      backgroundColor: isOwnMessage ? '#3B82F6' : '#F3F4F6',
      color: isOwnMessage ? 'white' : '#374151',
      marginLeft: isOwnMessage ? 'auto' : '0',
      marginRight: isOwnMessage ? '0' : 'auto',
      borderRadius: isOwnMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
      maxWidth: '80%'
    };
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-20 right-4 z-50">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between p-3 bg-blue-600 text-white rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="font-medium text-sm truncate max-w-48">{errandTitle}</span>
              <span className="text-xs opacity-75">({messages.length} messages)</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={toggleWidget}
                className="text-white hover:text-blue-100 transition-colors p-1"
                title="Expand Chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <button
                onClick={closeWidget}
                className="text-white hover:text-red-100 transition-colors p-1"
                title="Close Chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 h-96 bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-blue-600 text-white rounded-t-lg">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
          <span className="font-medium text-sm">{errandTitle}</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleWidget}
            className="text-white hover:text-blue-100 transition-colors"
            title="Minimize"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={closeWidget}
            className="text-white hover:text-red-100 transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-red-800 text-xs">
            {error}
          </div>
        )}

        {loading && messages.length === 0 ? (
          <div className="flex justify-center items-center h-20">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            <p className="text-sm">No messages yet</p>
            <p className="text-xs">Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="flex flex-col space-y-1">
              <div
                className="max-w-xs px-3 py-2 text-xs"
                style={getMessageStyle(message)}
              >
                <div className="flex items-center space-x-2 mb-1">
                  <span className="font-medium opacity-75">
                    {message.sender_name}
                  </span>
                  <span className="opacity-60">
                    {formatMessageTime(message.created_at)}
                  </span>
                </div>
                <p className="break-words">{message.message}</p>

                {/* File attachment */}
                {message.file_url && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                    <a
                      href={message.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-xs hover:underline"
                    >
                      <span>📎</span>
                      <span className="truncate">{message.file_name || 'File attachment'}</span>
                      {message.file_size && (
                        <span className="text-gray-500">
                          ({(Math.round((message.file_size / 1024) * 10) / 10).toFixed(1)} KB)
                        </span>
                      )}
                    </a>
                    {message.file_type?.startsWith('image/') && (
                      <img
                        src={message.file_url}
                        alt={message.file_name || 'Image'}
                        className="mt-2 max-w-full rounded-lg shadow-sm"
                        style={{ maxHeight: '150px' }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-3 border-t border-gray-200 bg-white rounded-b-lg">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <FileUploadButton
            errandId={errandId}
            onFileUploaded={async (fileUrl, fileName, fileType, fileSize) => {
              await sendMessage({
                errandId,
                message: `📎 ${fileName}`,
                messageType: 'file',
                fileUrl,
                fileName,
                fileType,
                fileSize
              });
              await fetchMessages(errandId);
              window.dispatchEvent(new CustomEvent('messageSent', { detail: { errandId } }));
            }}
            disabled={loading}
          />
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatWidget;
